/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { ActiveLink } from '@/components';
import getLinkProps from '@/lib/getLinkProps';
import Icon from '@/assets/icons';
import { favoriteBrandsSelector } from '../../store';
import styles from './CategoryTree.module.scss';

const CategoryName = ({ name, blank, color }) => {
	const words = name.split(' ');
	const lastWord = words[words.length - 1];
	const otherWords = words.slice(0, -1).join(' ');

	return blank ? (
		<span style={color !== '' ? { color: color } : {}}>
			{otherWords}{' '}
			<span className='with-icon'>
				{lastWord}
				<Icon name='blank' />
			</span>
		</span>
	) : (
		<span style={color !== '' ? { color: color } : {}}>{name}</span>
	);
};

const CategoryTree = ({ sidebar, savedQuery, parent, currentSlug, isSearch = false }) => {
	const ref = useRef();

	const favoriteBrands = useSelector(favoriteBrandsSelector);

	const getLink = useCallback(
		({ className, linkHref, slug, name, isBlanked, color }) => {
			return (
				<ActiveLink
					href={{ pathname: linkHref, query: savedQuery || {} }}
					as={{ pathname: slug, query: savedQuery || {} }}
					shallow
					disableActive
				>
					<a className={className}>
						<CategoryName name={name} blank={isBlanked} color={color} />
					</a>
				</ActiveLink>
			);
		},
		[savedQuery],
	);

	const getChildrens = useCallback(
		items => {
			return (
				<ul className={styles.container}>
					{items.map(({ name, slug, is_blanked, color = '' }) => {
						const [linkHref] = getLinkProps({
							type: isSearch ? 'search' : 'catalog',
							value: slug,
						});

						return (
							<li className={styles.item} key={slug}>
								{getLink({
									className: parent ? styles.linkChildWithParent : styles.linkChild,
									linkHref,
									slug,
									name,
									isBlanked: is_blanked,
									color,
								})}
							</li>
						);
					})}
				</ul>
			);
		},
		[parent, getLink, favoriteBrands],
	);

	const getCategories = useCallback(
		items => {
			return items.map(
				({ name, slug, childrens, is_blanked, id = null, color = '', isFav = false }, index) => {
					const [linkHref] = getLinkProps({ type: isSearch ? 'search' : 'catalog', value: slug });

					const favoriteBrandsChildren =
						parent && parent.brandId
							? []
							: childrens
									?.filter(item => favoriteBrands.includes(String(item.brandId)))
									.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)) || [];
					const childrenWithoutFavoutiteBrands =
						parent && parent.brandId
							? childrens
							: favoriteBrands.length > 0
							? childrens?.filter(item => !favoriteBrands.includes(String(item.brandId))) || []
							: childrens || [];

					return (
						<li
							className={clsx(
								styles.item,
								isFav && index === favouriteCategories.length - 1 && styles.itemFav,
								(currentSlug === slug || currentSlug == id) && styles.activeLi,
							)}
							key={slug}
						>
							{currentSlug === slug || currentSlug == id ? (
								<>
									<span ref={ref} className={parent ? styles.linkChildActive : styles.activeLink}>
										<span style={color !== '' ? { color: color } : {}}>{name}</span>
									</span>

									{favoriteBrandsChildren &&
										favoriteBrandsChildren.length > 0 &&
										getChildrens(favoriteBrandsChildren)}
									{childrenWithoutFavoutiteBrands &&
										childrenWithoutFavoutiteBrands.length > 0 &&
										getChildrens(childrenWithoutFavoutiteBrands)}
								</>
							) : (
								getLink({
									className: parent ? styles.linkChild : styles.link,
									linkHref,
									slug,
									name,
									isBlanked: is_blanked,
									color,
								})
							)}
						</li>
					);
				},
			);
		},
		[sidebar, parent, currentSlug, getChildrens, getLink, favoriteBrands],
	);

	const favouriteCategories = useMemo(
		() =>
			sidebar
				.filter(item => favoriteBrands.includes(String(item.brandId)))
				.map(item => ({ ...item, isFav: true }))
				.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)) ?? [],
		[sidebar, favoriteBrands],
	);

	const categoriesWithoutFavorites = useMemo(
		() => sidebar.filter(item => !favoriteBrands.includes(String(item.brandId))) ?? [],
		[sidebar, favoriteBrands],
	);

	const [linkHref] = getLinkProps({ type: isSearch ? 'search' : 'catalog', value: parent?.slug });

	useEffect(() => {
		setTimeout(() => {
			ref.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
		}, 0);
	}, []);

	return (
		<ul className={styles.container}>
			{parent ? (
				<>
					<li className={styles.item} key={parent.slug}>
						{getLink({
							className: styles.link,
							linkHref,
							slug: parent.slug,
							name: parent.name,
							isBlanked: parent.is_blanked,
							color: parent?.color ?? '',
						})}
					</li>

					<ul className={styles.container}>
						{getCategories([...favouriteCategories, ...categoriesWithoutFavorites])}
					</ul>
				</>
			) : (
				getCategories(sidebar)
			)}
		</ul>
	);
};

export default CategoryTree;
